<template>
  <v-dialog v-model="showDialog" width="500px">
    <v-card class="search-modal">
      <h2 class="mb-3">Search & Add Co-Owners</h2>
      <p class="mb-6">
        Only full (single, dual, lifetime, or lifetime dual) members, active for the last 5+
        years can be kennel owners.
      </p>
      <v-form class="search-form" @submit.prevent="search">
        <v-text-field
          outlined
          prepend-inner-icon="mdi-magnify"
          v-model="membershipID"
          label="Search Membership ID"
        ></v-text-field>
      </v-form>
      <p v-if="!searchResults.length && hasSearched">No users found</p>
      <v-data-table
        v-if="searchResults.length"
        :headers="headers"
        :items="searchResults"
        hide-default-footer
        hide-default-header
      >
        <template v-slot:item="row">
          <v-tooltip dark right>
            <template v-slot:activator="{ on, attrs }">
              <tr v-bind="attrs" v-on="on">
                <td>{{ row.item.firstName }} {{ row.item.lastName }}</td>
                <td>{{ row.item.memberId }}</td>
                <td>
                  <font-awesome-icon
                    icon="id-card"
                    :color="
                      row.item.canBeKennelCoowner == false ? 'red' : 'green'
                    "
                  />
                </td>
                <td>
                  <a
                    :class="{
                      'disabled-link': isValidCoOwner(row.item) == false,
                    }"
                    v-on:click="addCoOwner(row.item)"
                  >
                    <font-awesome-icon
                      icon="plus"
                      :color="isValidCoOwner(row.item) ? 'blue' : 'gray'"
                    />
                  </a>
                </td>
              </tr>
            </template>
            <span v-if="isValidCoOwner(row.item) == false"
              >This person hasn't been full active member for 5+ years and can't
              be kennel coowner.</span
            >
            <span v-else>This person can be added as a coowner.</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <p class="mb-6" v-if="selectedCoOwners.length">- or -</p>
      <v-btn
        color="primary"
        class="white--text"
        block
        :disabled="!selectedCoOwners.length"
        v-on:click="submit"
        >Continue</v-btn
      >
      <div v-if="selectedCoOwners.length">
        <h2 class="mt-8">Added Co-Owners</h2>
        <v-data-table
          :headers="headers"
          :items="selectedCoOwners"
          hide-default-footer
          hide-default-header
        >
          <template v-slot:item="row">
            <tr>
              <td>{{ row.item.firstName }} {{ row.item.lastName }}</td>
              <td>({{ row.item.memberId }})</td>
              <td>
                <font-awesome-icon
                  icon="id-card"
                  :color="
                    row.item.canBeKennelCoowner == false ? 'red' : 'green'
                  "
                />
              </td>
              <td>
                <a
                  :class="{
                    'disabled-link': isValidCoOwner(row.item) == false,
                  }"
                  v-on:click="removeCoOwner(row.item)"
                >
                  <font-awesome-icon icon="minus" />
                </a>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
      <p class="mt-8">
        Co-Owner will be emailed a link to sign the registration document.
        Registration is not complete until co-owner has signed. Co-Owners must
        be registered ASCA members.
      </p>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "throttle-debounce";

import registerKennelService from "@/services/portal/registerKennelService";
import { CoOwnerResult } from "@/types/portal/Registration";

export default Vue.extend({
  name: "AddCoOwnerModal",
  props: {
    changeCoOwnerInfo: Function,
    coOwnerDialog: Boolean,
    toggleCoOwnerModal: Function,
    coOwnerInfo: Array,
  },
  data: function () {
    return {
      showDialog: this.coOwnerDialog,
      hasSearched: false,
      searchResults: [] as CoOwnerResult[],
      selectedCoOwners: [...this.coOwnerInfo],
      membershipID: "",
      headers: [
        { text: "Name", align: "left", value: "name" },
        { text: "Member ID", align: "left", value: "memberId" },
      ],
    };
  },
  watch: {
    coOwnerDialog: function (newVal: boolean) {
      if (!newVal) {
        this.membershipID = "";
        this.hasSearched = false;
        this.searchResults = [] as CoOwnerResult[];
      }
      this.showDialog = newVal;
    },
    showDialog: function (newVal: boolean) {
      //this is handling situation when the user closes dialog
      //by clicking outside of it
      if (!newVal) {
        this.membershipID = "";
        this.hasSearched = false;
        this.searchResults = [] as CoOwnerResult[];
        (this.selectedCoOwners = [...this.coOwnerInfo]),
          this.toggleCoOwnerModal();
      }
    },
    membershipID: {
      handler: debounce(
        1000,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal || !newVal) return;
          this.search();
        }
      ),
      deep: true,
    },
  },
  methods: {
    search: function () {
      this.hasSearched = false;
      registerKennelService
        .searchCoOwner(this.membershipID)
        .then((response: CoOwnerResult[]) => {
          this.hasSearched = true;
          this.searchResults = response;
        });
    },
    isValidCoOwner(coowner: CoOwnerResult) {
      return coowner.canBeKennelCoowner;
    },
    addCoOwner(row: CoOwnerResult) {
      const found = this.selectedCoOwners.some(
        (el: any) => el.personId === row.personId
      );
      if (found) return;

      if (!row.canBeKennelCoowner) return;
      this.searchResults = [] as CoOwnerResult[];
      this.hasSearched = false;
      this.membershipID = "";
      let item = {
        firstName: row.firstName,
        lastName: row.lastName,
        memberId: row.memberId,
        personId: row.personId,
        canBeKennelCoowner: row.canBeKennelCoowner,
      };
      this.selectedCoOwners.push(item);
    },
    removeCoOwner(row: CoOwnerResult) {
      this.selectedCoOwners.splice(this.selectedCoOwners.indexOf(row), 1);
    },
    submit() {
      this.changeCoOwnerInfo(this.selectedCoOwners);
      this.showDialog = false;
    },
  },
});
</script>